/**
 * SelectrNavigation.tsx - Navigation-specific Selectr layout
 * 
 * CHANGE HISTORY:
 * - 251122_1535 danielbeer: Created for navigation menu with button layout
 * 
 * Features:
 * - Single-row button layout (like SideMenu)
 * - Disclosure triangles for groups
 * - Favorites toggle
 * - Icons and badges
 * - Keyboard navigation
 * - Filtr fuzzy search integration
 */

import React, { useState, useMemo } from 'react';
import type { SelectrItem, SelectrGroup } from './types';
import './SelectrNavigation.css';

export interface SelectrNavigationProps {
  items: SelectrItem[];
  currentItemId?: string | number;
  onItemClick: (item: SelectrItem) => void;
  onFavoriteToggle?: (item: SelectrItem, isFavorite: boolean) => void;
  searchable?: boolean;
  showFavorites?: boolean;
  showDisclosureTriangles?: boolean;
  grouped?: boolean;
  filterQuery?: string;
  className?: string;
}

export const SelectrNavigation: React.FC<SelectrNavigationProps> = ({
  items,
  currentItemId,
  onItemClick,
  onFavoriteToggle,
  searchable = true,
  showFavorites = true,
  showDisclosureTriangles = true,
  grouped = true,
  filterQuery = '',
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState(filterQuery);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Group items
  const groupedItems = useMemo(() => {
    const groups = new Map<string, SelectrItem[]>();
    
    items.forEach(item => {
      const groupKey = item.group || 'Other';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(item);
    });

    return Array.from(groups.entries()).map(([label, items], index) => ({
      id: `${label.toLowerCase().replace(/\s+/g, '-')}-${index}`,
      label,
      items,
      collapsed: collapsedGroups.has(label),
      collapsible: showDisclosureTriangles
    }));
  }, [items, collapsedGroups, showDisclosureTriangles]);

  // Filter items
  const filteredGroups = useMemo(() => {
    if (!searchQuery && !showFavoritesOnly) {
      return groupedItems;
    }

    return groupedItems.map(group => ({
      ...group,
      items: group.items.filter(item => {
        // Favorites filter
        if (showFavoritesOnly && !item.isFavorite) {
          return false;
        }

        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            item.label.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query) ||
            item.group?.toLowerCase().includes(query)
          );
        }

        return true;
      })
    })).filter(group => group.items.length > 0);
  }, [groupedItems, searchQuery, showFavoritesOnly]);

  // Toggle group collapse
  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Toggle favorite
  const handleFavoriteToggle = (item: SelectrItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const newFavoriteState = !item.isFavorite;
    onFavoriteToggle?.(item, newFavoriteState);
  };

  return (
    <div className={`selectr-navigation ${className}`}>
      {/* Search and Favorites Filter */}
      {(searchable || showFavorites) && (
        <div className="selectr-navigation__header">
          {searchable && (
            <div className="picker-search-wrapper" style={{ flex: 1 }}>
              <input
                type="text"
                className="selectr-navigation__search picker-search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="picker-search-clear"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          )}
          {showFavorites && (
            <button
              className={`selectr-navigation__favorites-toggle ${showFavoritesOnly ? 'active' : ''}`}
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              title={showFavoritesOnly ? 'Show all items' : 'Show favorites only'}
            >
              {showFavoritesOnly ? '★' : '☆'}
            </button>
          )}
        </div>
      )}

      {/* Navigation Items */}
      <div className="selectr-navigation__content">
        {filteredGroups.map(group => (
          <div key={group.id} className="selectr-navigation__group">
            {/* Group Header */}
            {grouped && (
              <div
                className="selectr-navigation__group-header"
                onClick={() => group.collapsible && toggleGroup(group.label)}
              >
                {group.collapsible && (
                  <span className={`disclosure-triangle ${group.collapsed ? 'collapsed' : 'expanded'}`}>
                    ▶
                  </span>
                )}
                <span className="group-label">{group.label}</span>
                <span className="group-count">{group.items.length}</span>
              </div>
            )}

            {/* Group Items */}
            {!group.collapsed && (
              <div className="selectr-navigation__group-items">
                {group.items.map(item => (
                  <button
                    key={item.id}
                    className={`selectr-navigation__item ${
                      item.id === currentItemId ? 'active' : ''
                    } ${item.disabled ? 'disabled' : ''}`}
                    onClick={() => !item.disabled && onItemClick(item)}
                    disabled={item.disabled}
                    title={item.description}
                  >
                    {/* Icon */}
                    {item.icon && (
                      <span className="item-icon">{item.icon}</span>
                    )}

                    {/* Label */}
                    <span className="item-label">{item.label}</span>

                    {/* Badge */}
                    {item.badge && (
                      <span className="item-badge">{item.badge}</span>
                    )}

                    {/* Favorite Star */}
                    {showFavorites && onFavoriteToggle && (
                      <span
                        className={`item-favorite ${item.isFavorite ? 'active' : ''}`}
                        onClick={(e) => handleFavoriteToggle(item, e)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleFavoriteToggle(item, e as any)}
                        title={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {item.isFavorite ? '★' : '☆'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Empty State */}
        {filteredGroups.length === 0 && (
          <div className="selectr-navigation__empty">
            {showFavoritesOnly ? 'No favorites yet' : 'No items found'}
          </div>
        )}
      </div>
    </div>
  );
};
